import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { DwLoggingService, DwSecurityUserService } from '@devwareapps/devware-cap';
import { CheckoutConfiguration } from '../../models/commerce.models';
import { PayPalCardField, PayPalCardFields, PayPalSdkService } from '../../services/paypal-sdk.service';

@Component({ selector: 'app-card-payment', templateUrl: './card-payment.component.html', styleUrls: ['./card-payment.component.scss'] })
export class CardPaymentComponent implements AfterViewInit, OnDestroy {
    @Input() configuration: CheckoutConfiguration;
    @Input() draftId: string;
    @Input() canPay = false;
    @Input() amount: number;
    @Input() billingAddress: any;
    @Input() createOrder: (draftId: string) => Promise<string>;
    @Input() approveOrder: (data: { orderID: string }, draftId: string) => Promise<void>;
    @Input() legacyPayment: (card: { name: string; number: string; expiry: string; cvc: string }, draftId: string) => Promise<void>;
    @Output() paymentError = new EventEmitter<string>();
    @Output() submissionChanged = new EventEmitter<boolean>();
    @Output() testPaymentRequested = new EventEmitter<void>();
    testCard = { name: '', number: '', expiry: '', cvc: '' };
    legacyCard = { name: '', number: '', expiry: '', cvc: '' };
    testCardsOpen = false;
    testCardNotice = '';
    cardName = '';
    readonly fakeCard = { label: 'Successful test payment', name: 'Test Streaker', number: '4111 1111 1111 1111', expiry: '12 / 30', cvc: '123' };
    readonly sandboxCards = [
        { label: 'Visa', name: 'Test Streaker', number: '4012 8888 8888 1881', expiry: '12 / 30', cvc: '123' },
        { label: 'Mastercard', name: 'Test Streaker', number: '2223 0000 4840 0011', expiry: '12 / 30', cvc: '123' },
        { label: 'American Express', name: 'Test Streaker', number: '3714 496353 98431', expiry: '12 / 30', cvc: '1234' },
    ];
    selectedTestCard = this.sandboxCards[0];
    async useTestCard(card = this.configuration?.testPayments ? this.fakeCard : this.sandboxCards[0]): Promise<void> {
        this.testCardsOpen = false;
        if (this.configuration?.testPayments) {
            this.cardName = this.defaultCardName || this.cardName;
            this.testCard = { name: this.cardName || card.name, number: card.number, expiry: card.expiry, cvc: card.cvc };
            this.testCardNotice = '';
        } else if (this.configuration?.payPalEnvironment === 'Sandbox') {
            this.selectedTestCard = card;
            this.cardName = card.name.startsWith('CCREJECT-') ? card.name : this.defaultCardName || card.name;
            this.testCardNotice = card.number + ' · Expiry ' + card.expiry + ' · CVC ' + card.cvc + ' · Name ' + this.cardName;
            try {
                await navigator.clipboard.writeText(card.number);
                this.testCardNotice = 'Card number copied. Paste into the secure card field. ' + this.testCardNotice;
            } catch (_) { this.testCardNotice = 'Copy this number into the secure card field. ' + this.testCardNotice; }
        }
    }
    async copyTestValue(value: string): Promise<void> {
        try { await navigator.clipboard.writeText(value); this.testCardNotice = 'Copied. Paste into the matching secure field.'; }
        catch (_) { this.testCardNotice = 'Select and copy the test value, then paste into the matching secure field.'; }
    }
    readonly sandboxFailures = [
        { name: 'Refused', trigger: 'CCREJECT-REFUSED' },
        { name: 'Insufficient funds', trigger: 'CCREJECT-IF' },
        { name: 'Expired', trigger: 'CCREJECT-EC' },
        { name: 'Security code failure', trigger: 'CCREJECT-CVV_F' },
        { name: 'Bank decline', trigger: 'CCREJECT-BANK_ERROR' },
        { name: 'Suspected fraud', trigger: 'CCREJECT-SF' },
        { name: 'Lost or stolen', trigger: 'CCREJECT-LS' },
        { name: 'Invalid account', trigger: 'CCREJECT-IA' },
        { name: 'Invalid or restricted number', trigger: 'CCREJECT-IRC' },
    ];
    @ViewChild('number', { static: true }) number: ElementRef<HTMLElement>;
    @ViewChild('expiry', { static: true }) expiry: ElementRef<HTMLElement>;
    @ViewChild('cvc', { static: true }) cvc: ElementRef<HTMLElement>;
    loading = true; available = false; submitting = false;
    loadFailed = false;
    ineligible = false;
    private destroyed = false;
    private form: PayPalCardFields;
    private fields: PayPalCardField[] = [];
    private get defaultCardName(): string {
        const user = this.security?.securityContext?.ApplicationUser;
        return [user?.FirstName, user?.LastName].filter(Boolean).join(' ').trim() || user?.FirstNameLastName || '';
    }
    constructor(private paypal: PayPalSdkService, private security: DwSecurityUserService, private dwLoggingService: DwLoggingService) {
        this.cardName = this.defaultCardName;
        this.testCard.name = this.cardName;
    }
    ngAfterViewInit(): void {
        if (this.configuration?.testPayments || this.configuration?.legacyPayPalCards) {
            this.loading = false; this.available = true; this.legacyCard.name = this.defaultCardName;
        } else { this.load(); }
    }
    async load(): Promise<void> {
        if (!this.cardName) { this.cardName = this.defaultCardName; }
        this.loading = true;
        this.available = false; this.loadFailed = false; this.ineligible = false;
        this.fields.forEach(field => { if (field.close) { field.close().catch(() => {}); } }); this.fields = [];
        const draftId = this.draftId;
        try {
            const sdk = await this.paypal.load(this.configuration);
            if (this.destroyed) { return; }
            if (!sdk.CardFields) { this.ineligible = true; return; }
            this.form = sdk.CardFields({
                createOrder: () => {
                    if (this.destroyed || !this.canPay || this.draftId !== draftId) { throw new Error('Review your order before payment.'); }
                    return this.createOrder(draftId);
                },
                onApprove: data => {
                    if (this.destroyed || this.draftId !== draftId) { throw new Error('Reload checkout before confirming payment.'); }
                    return this.approveOrder(data, draftId);
                },
                onError: error => {
                    this.logPaymentError('PayPal card payment failed.', error);
                    this.paymentError.emit('Card payment could not be completed. Check your details and select Pay to try again.');
                },
                style: { input: { 'font-size': '16px', 'font-family': 'Arial, sans-serif', 'line-height': '24px',
                    padding: '10px 12px', border: '1px solid #bdc8c1', 'border-radius': '6px',
                    'box-shadow': 'none', color: '#132c25' }, '.invalid': { color: '#c41e3a', border: '1px solid #c41e3a' } }
            });
            if (!this.form.isEligible()) { this.ineligible = true; return; }
            this.fields = [this.form.NumberField({ placeholder: 'Card number' }),
                this.form.ExpiryField({ placeholder: 'MM / YY' }), this.form.CVVField({ placeholder: 'CVC' })];
            const containers = [this.number, this.expiry, this.cvc];
            await Promise.all(this.fields.map((field, i) => field.render(containers[i].nativeElement)));
            if (!this.destroyed) { this.available = true; }
        } catch (error) {
            this.loadFailed = true;
            this.logPaymentError('PayPal card fields failed to initialize.', error);
            if (!this.destroyed) { this.paymentError.emit('Card payment fields could not load. Select Retry card fields to try again.'); }
        } finally { this.loading = false; }
    }
    async pay(): Promise<void> {
        if (!this.canPay || !this.available || this.submitting) { return; }
        if (this.configuration?.legacyPayPalCards && !this.configuration?.testPayments) {
            const card = { ...this.legacyCard };
            const expiry = this.normalizedExpiry(card.expiry);
            const validation = {
                nameInvalid: !card.name.trim(),
                numberInvalid: !/^[\d -]{13,23}$/.test(card.number),
                expiryInvalid: !expiry,
                securityCodeInvalid: !/^\d{3,4}$/.test(card.cvc)
            };
            if (Object.values(validation).some(invalid => invalid)) {
                this.logPaymentError('Legacy PayPal card validation failed before submission.', undefined, validation);
                this.paymentError.emit('Check your card name, number, expiration and security code.'); return;
            }
            card.expiry = expiry;
            this.legacyCard = { name: '', number: '', expiry: '', cvc: '' };
            this.submitting = true; this.submissionChanged.emit(true);
            try { await this.legacyPayment(card, this.draftId); }
            catch (error) {
                this.logPaymentError('Legacy PayPal card payment failed.', error);
                // CAP's messaging interceptor rethrows HTTP failures as DwErrorDetails.
                // Its message contains the API's participant-facing ProblemDetails detail.
                const apiMessage = error?.error?.detail ||
                    (error?.status >= 400 && error.status <= 599 ? error.message : null);
                this.paymentError.emit(typeof apiMessage === 'string' && apiMessage.trim()
                    ? apiMessage : 'We could not confirm payment. Check your order status before trying again.');
            } finally {
                card.name = card.number = card.expiry = card.cvc = '';
                this.submitting = false;
                if (!this.destroyed) { this.submissionChanged.emit(false); }
            }
            return;
        }
        if (this.configuration?.testPayments) {
            if (!this.testCard.name.trim() || this.testCard.number.replace(/\s/g,'') !== this.fakeCard.number.replace(/\s/g,'') ||
                !this.normalizedExpiry(this.testCard.expiry) || !/^\d{3,4}$/.test(this.testCard.cvc)) {
                this.paymentError.emit('Use the test card 4111 1111 1111 1111, a name, expiration in MM / YY format, and a 3 or 4 digit CVC.'); return;
            }
            this.testCard = { name: '', number: '', expiry: '', cvc: '' };
            this.testPaymentRequested.emit(); return;
        }
        this.submitting = true;
        this.submissionChanged.emit(true);
        try { await this.form.submit({ name: this.cardName, billingAddress: this.billingAddress }); }
        catch (error) {
            this.logPaymentError('PayPal card payment submission failed.', error);
            if (!this.destroyed) { this.paymentError.emit('Check your card details and order status before trying again.'); }
        }
        finally { this.submitting = false; if (!this.destroyed) { this.submissionChanged.emit(false); } }
    }
    formatExpiry(value: string): string {
        if (!/^[0-9\s/]*$/.test(value || '')) { return value; }
        const digits = (value || '').replace(/\D/g, '');
        return digits.length > 2 ? digits.slice(0, 2) + '/' + digits.slice(2) : digits;
    }
    private normalizedExpiry(value: string): string {
        const match = /^(0[1-9]|1[0-2])(?:\s*\/\s*)?((?:20)?[0-9]{2})$/.exec((value || '').trim());
        if (!match) { return null; }
        const year = 2000 + Number(match[2].slice(-2));
        if (Date.UTC(year, Number(match[1]), 1) <= Date.now()) { return null; }
        return match[1] + '/' + match[2].slice(-2);
    }
    private logPaymentError(message: string, error?: any, validation?: {
        nameInvalid: boolean; numberInvalid: boolean; expiryInvalid: boolean; securityCodeInvalid: boolean;
    }): void {
        // Provider exceptions may contain card data in messages, stacks or HTTP payloads.
        const status = Number.isInteger(error?.status) && error.status >= 0 && error.status <= 599 ? error.status : undefined;
        try {
            this.dwLoggingService.logError(message, new Error(message), {
                payPalEnvironment: this.configuration?.payPalEnvironment,
                currency: this.configuration?.currency,
                hasClientId: !!this.configuration?.payPalClientId,
                hasMerchantId: !!this.configuration?.payPalMerchantId,
                testPayments: this.configuration?.testPayments,
                legacyPayPalCards: this.configuration?.legacyPayPalCards,
                httpStatus: status,
                validation
            }, true);
        } catch (_) { /* Logging must not interrupt checkout or obscure the participant's error. */ }
    }
    ngOnDestroy(): void {
        this.destroyed = true;
        this.legacyCard = { name: '', number: '', expiry: '', cvc: '' };
        this.fields.forEach(field => { if (field.close) { field.close().catch(() => {}); } });
    }
}

